import { type Html, html, UnsafeHtml, View } from 'rune-ts';
import klass from './Title.module.scss';
import type { Tabs } from '../../cells/Tabs/Tabs';
import { Tooltip } from '../Tooltip/Tooltip';
import { font } from '../../../../shared/typography';
import { htmlIf } from '../../../../shared/util';
import { SectionTabs } from '../../../../shared/components/atoms/SectionTabs/SectionTabs';

type TitleSizeType = 'xLarge' | 'large' | 'Large' |'medium' | 'semiMedium' | 'small' | 'xSmall' | 'xxSmall';
type TitleThemeType = 'light' | 'dark';

export interface TitleProps {
  title: string | Html | UnsafeHtml | Tabs | SectionTabs;
  sub_title?: string;
  tooltip?: Tooltip;
  custom_klass?: string;
  /**
   *  default: xLarge
   */
  size?: TitleSizeType;
  theme?: TitleThemeType;
  title_color?: string;
  sub_title_color?: string;
}

export class Title extends View<TitleProps> {
  static readonly sizeOptions = {
    xLarge: {
      title_font: font('24_bold'),
      sub_title_font: font('16_medium'),
      size_class: klass.xLarge,
    },
    large: {
      title_font: font('18_semibold'),
      sub_title_font: font('14_medium'),
      size_class: klass.large,
    },
    /**
     * https://www.figma.com/design/Top00pi5q5ylSxEviOOciV/MarppleShop-Design-System?node-id=24573-30101&m=dev
     */
    Large: {
      title_font: font('18_semibold'),
      size_class: klass.Large,
    },
    medium: {
      title_font: font('20_bold'),
      size_class: klass.medium,
    },
    semiMedium: {
      title_font: font('18_bold'),
      size_class: klass.semiMedium,
    },
    small: {
      title_font: font('16_bold'),
      size_class: klass.small,
    },
    xSmall: {
      title_font: font('16_bold'),
      size_class: klass.xSmall,
    },
    xxSmall: {
      title_font: font('14_bold'),
      size_class: klass.xxSmall,
    },
  } as const;

  static readonly themeOptions = {
    light: klass.light,
    dark: klass.dark,
  } as const;

  protected override template(data: TitleProps) {
    const { title, title_color, tooltip, custom_klass, sub_title, size = 'xLarge', theme = 'light' } = data;

    const { title_font, size_class } = Title.sizeOptions[size];
    const theme_class = Title.themeOptions[theme];

    return html`
      <div class="${klass.page_title} ${size_class} ${theme_class} ${custom_klass}">
        ${htmlIf(this._makeSubtitleHtml(size), !!sub_title)}

        <div class="${klass.title}"${title_color ? ' style="color: ${title_color};' : ''}">
          <h2 class="${title_font}">${title}</h2>
          ${tooltip || ''}
        </div>

      </div>
    `;
  }

  public setTitle(title: string) {
    this.data.title = title;
    this.redraw();
  }

  private _makeSubtitleHtml(size: TitleSizeType) {
    const { sub_title, sub_title_color = '' } = this.data;

    if (size === 'xLarge' || size === 'large') {
      const size_option = Title.sizeOptions[size];

      return html`<p
        class="${klass.sub_title} ${size_option.sub_title_font}"
        style="color: ${sub_title_color};"
      >
        ${sub_title}
      </p>`;
    }

    return html``;
  }
}
